import * as React from 'react';
import { useReactToPrint } from 'react-to-print';
import styled from 'styled-components';
import { ActionPlanType, ActionPlanView, ActionPointData, permissionGroupHelper, PublicActionPlanLink, PublicAmorgUser, rng } from '../../../amorg-common';
import { fieldValidation } from '../../../amorg-common/modules/fieldValidation/fieldValidation';
import { assignActionPlanOwner, createActionPlanTopic, deleteActionPlan, getActionPlanLinks, getActionPlanViewById, updateActionPlan } from '../../../api/actionPlanClientApi';
import { getActivity, updateActivity } from '../../../api/activityClientApi';
import { Badge } from '../../../designSystem/Badge';
import { Label } from '../../../designSystem/Label';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { PageHeader } from '../../../designSystem/PageHeader';
import { Tabber } from '../../../designSystem/Tabber';
import { ToggletipTheOne } from '../../../designSystem/ToggletipTheOne';
import { NoUserAvatar } from '../../../designSystem/avatar/NoUserAvatar';
import { UserAvatar } from '../../../designSystem/avatar/UserAvatar';
import { Button } from '../../../designSystem/button/Button';
import { ButtonRow } from '../../../designSystem/button/ButtonRow';
import { colors } from '../../../designSystem/colors/color';
import { ToolBarButton, ToolBarContainer } from '../../../designSystem/compositeComponents/ButtonToolBar';
import { DeleteConfirmationModal } from '../../../designSystem/compositeComponents/DeleteConfirmationButton';
import { EditableText } from '../../../designSystem/compositeComponents/EditableText';
import { EditableTextArea } from '../../../designSystem/compositeComponents/EditableTextArea';
import { FileLinkCommentPanel } from '../../../designSystem/compositeComponents/FileLinkCommentPanel';
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { UserSelectorAvatarButton } from '../../../designSystem/compositeComponents/itemSelector/UserSelectorAvatarButton';
import { StartEndDateFields } from '../../../designSystem/inputs/StartEndDateFields';
import { H2 } from '../../../designSystem/typography/Heading';
import { userUtils } from '../../../utils/userUtils';
import PrintActionPlanView from './PrintActionPlanPage';
import { ProtectedPageContext } from '../../_contexts/ProtectedPageContext';
import { ActionPlanContextPanel } from './ActionPlanContextPanel';
import { ActionPlanLinkEditor } from './ActionPlanLinkEditor';
import { ActionPlanStatus, ActionPlanStatusCard } from './ActionPlanStatusCard';
import { SurveyTopicListButton } from './ActionPlanSurveyTopicList';
import { ActionPlanTopicList } from './ActionPlanTopicList';

type State = {
    actionPlan: ActionPlanView | null,
    links: PublicActionPlanLink[];
};

type ActionPlanViewProps = {
    actionPlanId: string,
    backLink: { text: string, url: string; },
    onBack?: () => void;
};
export default function ActionPlanView (props: ActionPlanViewProps): JSX.Element
{
    const [state, setState] = React.useState<State>({
        actionPlan: null,
        links: []
    });
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [selectedTab, setSelectedTab] = React.useState<'files' | 'links' | 'comments'>('files');

    React.useEffect(function ()
    {
        const getData = async () =>
        {
            await reloadFullState();
            setLoading(false);
        };
        getData();
    }, [props.actionPlanId]);

    const activeUser = React.useContext(ProtectedPageContext).user;

    async function reloadFullState ()
    {

        const actionPlan = await getActionPlanViewById(props.actionPlanId);
        if(!actionPlan)
            return;

        const links = await getActionPlanLinks(actionPlan.id);

        const newState: State = {
            actionPlan,
            links
        };

        setState(newState);

        return newState;
    }

    async function reloadActionPlan ()
    {
        setLoading(true);
        const actionPlan = await getActionPlanViewById(props.actionPlanId);

        setState({
            ...state,
            actionPlan
        });
        setLoading(false);
    }


    async function handleSaveTitle (value: string)
    {
        if(!state.actionPlan)
            return;

        setLoading(true);
        if ((await updateActionPlan(state.actionPlan.id, value, state.actionPlan.description)).isSuccess)
            reloadFullState();
        setLoading(false);
    }

    async function handleSaveDescription (value: string)
    {
        if(!state.actionPlan)
            return;

        setLoading(true);
        if ((await updateActionPlan(state.actionPlan.id, state.actionPlan.title, value)).isSuccess)
            reloadFullState();
        setLoading(false);
    }

    async function handleClickCreateTopic ()
    {
        if(!state.actionPlan)
            return;

        setLoading(true);
        if ((await createActionPlanTopic(rng.getRandomId(), state.actionPlan.id)).isSuccess)
            reloadActionPlan();
        setLoading(false);
    }

    async function handleDateChange (start: Date, end: Date)
    {
        if(!state.actionPlan)
            return;

        setLoading(true);
        const activity = await getActivity(state.actionPlan.activityId);
        if (activity)
        {
            const result = await updateActivity(activity.id, activity.title, activity.description, start, end, activity.location);
            if (result.isSuccess)
            {
                reloadFullState();
            }
            else if (result.errorMessage === 'updateActivity_start_dependency_violated'
                || result.errorMessage === 'updateActivity_end_dependency_violated')
            {

            }
        }
        setLoading(false);
    }

    async function handleChangeOwner (user: PublicAmorgUser | null)
    {
        const result = await assignActionPlanOwner(props.actionPlanId, user?.id || '');
        if (result.isSuccess)
        {
            reloadFullState();
        }
    }

    async function handleClickDeleteActionPlan ()
    {
        if(!state.actionPlan)
            return;

        setLoading(true);
        const deleteResult = await deleteActionPlan(state.actionPlan.id);
        if (deleteResult.isSuccess)
            props.onBack?.();        
        setLoading(false);
    }

    const printRef = React.useRef(null);
    const printFn = useReactToPrint({
        contentRef: printRef,
        documentTitle: state.actionPlan?.title || "Handleplan"
    });

    const planIsLoaded = state.actionPlan !== null;

    const isMember = activeUser && permissionGroupHelper.hasMinimumPermissionGroup(activeUser.permissionGroupId, 'member');
    const canSelectOwner = planIsLoaded && isMember;

    const dateActionPlanTypes: ActionPlanType[] = ['Independent', 'Article'];
    const showDate = dateActionPlanTypes.includes(state.actionPlan ? state.actionPlan.type : 'Independent');

    const allActionPoints: ActionPointData[] = state.actionPlan?.topics.flatMap(t => t.actionPoints) || [];
    const deadlinePassedActionPoints = allActionPoints.filter(ap => !ap.done && ap.deadline && ap.deadline < new Date());

    let status: ActionPlanStatus = 'in-progress';
    if (state.actionPlan?.topics.length === 0)
    {
        status = 'no-topics';
    }
    else if (allActionPoints.length === 0)
    {
        status = 'no-actionpoints';
    }
    else
    {

        if (allActionPoints.every(ap => ap.done))
            status = 'done';
        else if (deadlinePassedActionPoints.length > 0)
            status = 'deadline-passed';
        else
            status = 'in-progress';
    }

    return (
        <>
            {!state.actionPlan &&
                <LoadingPageOverlay loading={loading} />
            }

            {state.actionPlan &&
                <>
                    <PrintActionPlanView
                        actionPlan={state.actionPlan}
                        innerRef={printRef} />

                    <Flex flexDirection='column' gap={16}>

                        <PageHeader
                            headerText={'handleplan'}
                            headerStyle='secondary-gray'
                            buttons={
                                <ToolBarContainer>
                                    <ToolBarButton
                                        icon='interface_actions_print'
                                        tooltip='Print'
                                        variant='text'
                                        onClick={printFn}
                                    />
                                    {isMember &&
                                        <ToolBarButton
                                            icon="interface_actions_delete"
                                            tooltip="Slet handleplan"
                                            onClick={() => setShowDeleteModal(true)}
                                            variant="destructive-text"
                                        />
                                    }
                                </ToolBarContainer>
                            }
                            breadCrumbs={[props.backLink]}
                        />

                        <ActionPlanStatusCard status={status} actionPoints={allActionPoints} />

                        <StyledActionPlanContainer singleColumn={!isMember}>

                            <StyledActionPlanHeaderRow>
                                <Flex justifyContent='space-between' alignItems='flex-end'>
                                    <div style={{ flexGrow: '1' }}>
                                        <EditableText
                                            value={state.actionPlan.title}
                                            placeholder='handleplanens titel'
                                            ariaLabel="handleplanens titel"
                                            styleMode='H2'
                                            validator={(currentValue) =>
                                                fieldValidation.activity.title(currentValue)
                                                    ? { valid: true }
                                                    : { valid: false, errorMessage: `Ugyldig titel. Skal udfyldes med maks. 100 (${currentValue.length}) tegn.` }
                                            }
                                            onSave={(value) => fieldValidation.activity.title(value) && handleSaveTitle(value)}
                                        />
                                    </div>
                                    <div>
                                    {activeUser &&
                                        <ActionPlanLinkEditor
                                            siteId={activeUser.siteId}
                                            actionPlanId={state.actionPlan.id}
                                            linkContext={{ type: 'actionPlan' }}
                                            link={state.links.find(l => l.configuration.type === 'actionPlan')}
                                            onUpdated={() => reloadFullState()}
                                        />
                                    }
                                    </div>
                                </Flex>
                            </StyledActionPlanHeaderRow>

                            <StyledActionPlanUpperLeftColumn rightBorder={!isMember}>
                                <Flex justifyContent='flex-start' gap={32}>

                                    <Flex flexDirection={"column"} alignItems='flex-start' gap={4}>
                                        <Flex gap={8} alignItems='center' style={{minWidth:'110px', minHeight:'24px'}}>
                                            <Label formIndent>Ansvarlig</Label>
                                            {state.actionPlan.owner?.type === 'RegularUser' && state.actionPlan.owner.userId !== activeUser.userId &&
                                                <ToggletipTheOne icon='warning' iconColor={colors.icon.warning}>Da brugeren der er valgt som ansvarlig ikke har AMO medlemsrettigheder, kan han eller hun kun tilgå denne handleplan, og anden eksplicit tilvalgt funktionalitet</ToggletipTheOne>
                                            }
                                        </Flex>
                                        {permissionGroupHelper.hasMinimumPermissionGroup(activeUser.permissionGroupId, 'member')
                                            ?   <UserSelectorAvatarButton
                                                    disabled={!canSelectOwner}
                                                    selectedUserId={state.actionPlan.owner?.userId || null}
                                                    onUserChange={handleChangeOwner}
                                                />
                                            :   state.actionPlan.owner
                                                    ? <UserAvatar user={{
                                                        displayName: state.actionPlan.owner.fullName,
                                                        initials: userUtils.getInitials(state.actionPlan.owner.fullName)
                                                    }} />
                                                    : <NoUserAvatar tooltip="Ingen valgt" />
                                        }
                                    </Flex>



                                    {showDate &&
                                        <Flex flexDirection={"column"} gap={4}>
                                            <div style={{minHeight:'24px'}}><Label formIndent>Periode</Label></div>
                                            <StartEndDateFields noBorder
                                                disabled={!isMember}
                                                start={state.actionPlan.start}
                                                end={state.actionPlan.end}
                                                onChanged={handleDateChange}
                                            />
                                        </Flex>
                                    }
                                </Flex>
                            </StyledActionPlanUpperLeftColumn>

                            <StyledActionPlanLowerLeftColumn rightBorder={!isMember}>
                                <div>
                                    <Label formIndent>Formål</Label>
                                    <EditableTextArea
                                        ariaLabel='Beskrivelse'
                                        placeholder='Indsæt beskrivelse...'
                                        value={state.actionPlan.description}
                                        validator={(currentValue) =>
                                        {
                                            return fieldValidation.actionPlan.description(currentValue)
                                                ? { valid: true }
                                                : { valid: false, errorMessage: `Ugyldig titel. Maks. 200 (${currentValue.length}) tegn.` };
                                        }}
                                        onSave={(value, isValid) =>
                                        {
                                            if (isValid)
                                                handleSaveDescription(value);
                                        }}
                                    />
                                </div>
                                <Flex justifyContent="flex-end" gap={16}>
                                    <Badge count={allActionPoints.filter(ap => ap.done).length}
                                        variant="success"
                                        tooltip="Fuldførte handlepunkter" 
                                    />
                                    
                                    <Badge count={allActionPoints.filter(ap => !ap.done && (ap.deadline === null || ap.deadline > new Date())).length}
                                        variant="pending"
                                        tooltip="Åbne handlepunkter" 
                                    />
                                    <Badge count={allActionPoints.filter(ap => !ap.done && ap.deadline !== null && ap.deadline <= new Date()).length}
                                        variant="warning"
                                        tooltip="Handlepunkter med overskredet deadline" 
                                    />
                                    
                                </Flex>
                            </StyledActionPlanLowerLeftColumn>
                            
                            {isMember &&
                                <StyledActionPlanRightColumn>
                                    {isMember &&
                                        <Flex flexDirection='column' justifyContent='stretch' style={{ height: '100%' }}>
                                            <Flex style={{ flexGrow: '0' }} alignItems='center'>
                                                <Tabber variant='text-minimal' inline
                                                    selectedTabName={selectedTab}
                                                    items={[
                                                        { tabName: 'files', text: 'Vedhæftede filer' },
                                                        { tabName: 'links', text: 'Links' },
                                                        { tabName: 'comments', text: 'Kommentarer' }
                                                    ]}
                                                    onClick={(tabName) => setSelectedTab(tabName as 'files' | 'links' | 'comments')}
                                                />
                                                <ToggletipTheOne>
                                                    Filer, links og kommentarer er ikke tilgængelige når handleplanen tilgås via et delt link
                                                </ToggletipTheOne>
                                            </Flex>

                                            <Flex justifyContent='stretch' grow="1">
                                                {['files', 'links', 'comments'].includes(selectedTab) &&
                                                    <FileLinkCommentPanel stretch
                                                        cardVariant="none"
                                                        selectedTab={selectedTab as 'files' | 'links' | 'comments'}
                                                        hideTabber
                                                        maxHeightPx={200}
                                                        fileReference={{ type: 'ActionPlan', id: state.actionPlan.id }}
                                                        linkReference={{ type: 'ActionPlan', id: state.actionPlan.id }}
                                                        commentReference={{ type: 'ActionPlan', id: state.actionPlan.id }}
                                                    />
                                                }
                                            </Flex>
                                        </Flex>
                                    }
                                    
                                </StyledActionPlanRightColumn>
                            }
                        </StyledActionPlanContainer>
                        
                        <DeleteConfirmationModal
                            showModal={showDeleteModal}
                            buttonText="Slet handleplan"
                            confirmationHeading="Slet handleplan?"
                            confirmationText='Er du sikker på at du vil slette handleplanen?'
                            requireKey={true}
                            onConfirmDelete={handleClickDeleteActionPlan}
                            onCancel={() => setShowDeleteModal(false)} />




                        <ActionPlanContextPanel 
                            actionPlan={state.actionPlan}
                            onUpdated={reloadFullState} />

                        <div style={{margin:'0px 0px 0px 40px'}}>
                            <H2 style={{ margin: '0px 0px 16px 0px' }}>Handleplanens emner</H2>
                            <ActionPlanTopicList
                                siteId={activeUser?.siteId || ''}
                                actionPlan={state.actionPlan}
                                links={state.links}
                                onUpdated={reloadFullState}
                            />
                        </div>



                        <Flex flexDirection="column" gap={8}>
                            
                            {state.actionPlan?.type === 'WPA' && 
                                state.actionPlan.surveyData.surveyAnswerCount > 0 &&

                                <SurveyTopicListButton 
                                    actionPlanId={state.actionPlan.id}
                                    onUpdated={reloadFullState} />
                            }

                            <ButtonRow variant='stretched'>
                                <Button
                                    variant='outlined'
                                    icon="actions_plus"
                                    size={"42px"}
                                    onClick={handleClickCreateTopic}
                                    text={"Opret emne"}
                                />
                            </ButtonRow>

                        </Flex>


                        
                    </Flex>

                </>
            }
        </>
    );
}

const StyledActionPlanContainer = styled.div<{singleColumn:boolean}>`
    display:grid;
    grid-template-columns: ${props => props.singleColumn ? '1fr' : '1fr 600px'};
    grid-template-rows: min-content min-content 1fr;
    //margin-right: 36px;
    transition: all .5s linear;
`;

const StyledActionPlanHeaderRow = styled.div`
    grid-column: 1/3;
    grid-row: 1/2;
    padding: 0px 16px 0px 0px;
`;

const StyledActionPlanUpperLeftColumn = styled.div<{rightBorder?:boolean}>`
    grid-column: 1/2;
    grid-row: 2/3;
    border-top: ${props => props.theme.styles.card.variants.light.default.border};
    border-left: ${props => props.theme.styles.card.variants.light.default.border};
    border-top-left-radius: ${props => props.theme.styles.card.borderRadius};
    padding: 16px;
    ${props => props.rightBorder && `
        border-right: ${props => props.theme.styles.card.variants.light.default.border};
        border-top-right-radius: ${props => props.theme.styles.card.borderRadius};    
    `}
`;

const StyledActionPlanLowerLeftColumn = styled.div<{rightBorder?:boolean}>`
    grid-column: 1/2;
    grid-row: 3/4;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    border-left: ${props => props.theme.styles.card.variants.light.default.border};
    border-bottom: ${props => props.theme.styles.card.variants.light.default.border};
    border-bottom-left-radius: ${props => props.theme.styles.card.borderRadius};
    ${props => props.rightBorder && `
        border-right: ${props => props.theme.styles.card.variants.light.default.border};
        border-bottom-right-radius: ${props => props.theme.styles.card.borderRadius};    
    `}
    padding: 16px 16px 8px 16px;
`;

const StyledActionPlanRightColumn = styled.div`
    grid-column: 2/3;
    grid-row: 2/4;
    border: ${props => props.theme.styles.card.variants.light.default.border};
    border-top-right-radius: ${props => props.theme.styles.card.borderRadius};
    border-bottom-right-radius: ${props => props.theme.styles.card.borderRadius};
    padding: 0px 16px 8px 16px;
`;